import {
  MultiTurnInteractionConfiguration,
  InitialMessage,
  AssistantContent,
  MessageRole,
} from '@scale/shared/types/chat';
import { useState } from 'react';
import { Typography, Tooltip, Icon, IconProps } from '@scale/scaleui';
import Paper from '@mui/material/Paper';
import clsx from 'clsx';
import MarkdownToolbar from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/toolbar/MarkdownToolbar';
import MarkdownEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/MarkdownEditorContext';
import PlainTextEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/PlainTextEditorContext';
import {
  Editor,
  EditorContainer,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';

interface InitialMessagesTemplateCreatorProps {
  params: MultiTurnInteractionConfiguration;
}

const CUSTOM_TEXT_PLACEHOLDER = 'Set a constant value';

// TODO: Move type + TextMarkdownEditor component to a shared location and reuse with ResponseTemplateCreatorStep
type EditorType = 'milkdown' | 'plain-text';

function TextMarkdownEditor({
  value,
  label,
  setValue,
}: {
  value: string;
  label: string;
  setValue: (text: string) => void;
}) {
  const [editorType, setEditorType] = useState<EditorType>('plain-text');

  const markdownEditorComponent = (
    <MarkdownEditorContext initialContent={value}>
      <EditorContainer>
        <MarkdownToolbar />
        <Editor
          content={value}
          onContentChange={(content: string) => {
            setValue(content);
          }}
        />
      </EditorContainer>
    </MarkdownEditorContext>
  );

  const plainTextEditorComponent = (
    <PlainTextEditorContext initialContent={value}>
      <EditorContainer>
        <Editor
          content={value}
          placeholder={CUSTOM_TEXT_PLACEHOLDER}
          onContentChange={(content: string) => {
            setValue(content);
          }}
        />
      </EditorContainer>
    </PlainTextEditorContext>
  );

  return (
    <Paper className="p-2" elevation={2}>
      <Typography.Subtitle3>{label}</Typography.Subtitle3>
      <div style={{ overflowAnchor: 'none' }}>
        <div className="flex gap-2 py-1">
          <Tooltip
            side="top"
            text="Toggle between markdown and plain text editors"
            target={
              <div
                className={clsx(
                  'cursor-pointer z-10 mb-1 text-neutral-500 rounded-md h-[25px] flex justify-center items-center px-2 gap-1',
                  {
                    'text-primary-400 bg-primary-100': editorType === 'milkdown',
                  },
                )}
                onClick={() => {
                  setEditorType(editorType === 'milkdown' ? 'plain-text' : 'milkdown');
                }}
              >
                <Icon size="sm" icon="pencil-ruler" className="rounded-full" /> Markdown
              </div>
            }
          />
        </div>
        {editorType === 'milkdown' ? (
          <div onKeyDown={e => e.stopPropagation()}>{markdownEditorComponent}</div>
        ) : (
          plainTextEditorComponent
        )}
      </div>
    </Paper>
  );
}

const InitialMessagesTemplateCreator: React.FC<InitialMessagesTemplateCreatorProps> = ({
  params,
}) => {
  const [initialMessages, setInitialMessages] = useState<InitialMessage[]>(
    params.initialMessages ?? [],
  );

  const setInitialMessagesIndex = (index: number, message: InitialMessage) => {
    const newInitialMessages = [...initialMessages];
    newInitialMessages[index] = message;
    setInitialMessages(newInitialMessages);
  };

  const renderInitialMessage = (
    message: InitialMessage,
    setMessage: (message: InitialMessage) => void,
  ) => {
    const setOptionAtIndex = (index: number, message: InitialMessage, option: AssistantContent) => {
      const newOptions = [...message.options!];
      newOptions[index] = option;
      setMessage({ ...message, options: newOptions });
    };

    return (
      <div className="space-y-3 my-2 mx-1" onKeyDown={e => e.stopPropagation()}>
        <TextMarkdownEditor
          value={message.content ?? ''}
          label="Content"
          setValue={content => setMessage({ ...message, content })}
        />
        {message.options?.map((option, optionIndex) => {
          return (
            <div key={option.id}>
              <TextMarkdownEditor
                value={option.content}
                label={`Option: ${option.id}`}
                setValue={text => {
                  setOptionAtIndex(optionIndex, message, { ...option, content: text });
                }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const getMessageRoleIcon = (role: MessageRole, size: IconProps['size'] = 'md') => {
    let tooltipText: string = '';
    let icon: IconProps['icon'] = ['fas', 'user'];
    switch (role) {
      case MessageRole.User:
        tooltipText = 'User';
        icon = ['fas', 'user'];
        break;
      case MessageRole.Assistant:
        tooltipText = 'Assistant';
        icon = ['fas', 'robot'];
        break;
      case MessageRole.System:
        tooltipText = 'System';
        icon = ['fas', 'computer-classic'];
        break;
      case MessageRole.Model:
        tooltipText = 'Model';
        icon = ['fas', 'microchip'];
        break;
    }

    return (
      <Tooltip
        text={tooltipText}
        target={
          <Icon size={size} icon={icon} hasBackground variant="white" className="rounded-full" />
        }
      />
    );
  };

  return (
    <div className="flex-1 flex flex-col min-w-[1080px] overflow-y-scroll">
      <div className="gap-4">
        {initialMessages.map((initialMessage, index) => (
          <div className="space-y-2 my-6" key={index}>
            <div className="flex flex-row items-center gap-2">
              {getMessageRoleIcon(initialMessage.role)}
              <Typography.Subtitle2>Initial Message: {initialMessage.role}</Typography.Subtitle2>
            </div>
            {renderInitialMessage(initialMessage, message =>
              setInitialMessagesIndex(index, message),
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InitialMessagesTemplateCreator;
